import React, { Component } from 'react';
import PropTypes from 'prop-types'
import { shallowEqual } from 'recompose'
import styled from 'styled-components'
import MarkerClusterer from './markerclusterer'
import { icon, iconActive } from './MapIcons'
import { parseGeolocationForCourse } from 'utils/content-utils'

class Map extends Component {

  static propTypes = {
    google: PropTypes.object,
    backgroundColor: PropTypes.string,
    center: PropTypes.shape({
      lat: PropTypes.number,
      lng: PropTypes.number,
    }),
    clickableIcons: PropTypes.bool,
    disableDefaultUI: PropTypes.bool,
    disableDoubleClickZoom: PropTypes.bool,
    draggable: PropTypes.bool,
    draggableCursor: PropTypes.string,
    draggingCursor: PropTypes.string,
    fullscreenControl: PropTypes.bool,
    fullscreenControlOptions: PropTypes.object,
    gestureHandling: PropTypes.oneOf(['cooperative', 'greedy', 'none', 'auto']),
    heading: PropTypes.number,
    keyboardShortcuts: PropTypes.bool,
    mapTypeControl: PropTypes.bool,
    mapTypeControlOptions: PropTypes.object,
    mapTypeId: PropTypes.oneOf(['roadmap', 'satellite', 'hybrid', 'terrain']),
    maxZoom: PropTypes.number,
    minZoom: PropTypes.number,
    noClear: PropTypes.bool,
    panControl: PropTypes.bool,
    panControlOptions: PropTypes.object,
    rotateControl: PropTypes.bool,
    rotateControlOptions: PropTypes.object,
    scaleControl: PropTypes.bool,
    scaleControlOptions: PropTypes.object,
    scrollWheel: PropTypes.bool,
    signInControl: PropTypes.bool,
    streetView: PropTypes.object,
    streetViewControl: PropTypes.bool,
    streetViewControlOptions: PropTypes.object,
    styles: PropTypes.array,
    tilt: PropTypes.number,
    zoom: PropTypes.number,
    zoomControl: PropTypes.bool,
    zoomControlOptions: PropTypes.object,
    activeCourses: PropTypes.array,
  }

  state = {
    finnishCourses: []
  }

  componentDidMount() {
    this.loadMap();
    const {
      disableFiClubLoading
    } = this.props
    if(disableFiClubLoading){
      return
    }
    this.loadFinnishCourses();
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      courses,
      activeCourses,
    } = this.props
    const {
      finnishCourses
    } = this.state
    if (this.map && courses.length) {
      this.drawCluster(courses, activeCourses, finnishCourses)
    }
  }

  shouldComponentUpdate(nextProps, nextState){
    if (
      (
      !shallowEqual(nextProps.courses, this.props.courses) || 
      !shallowEqual(nextProps.activeCourses, this.props.activeCourses) || 
      !shallowEqual(nextState.finnishCourses, this.state.finnishCourses)
      ) && this.map) {
      return true
    }
    return false
  }

  hasPosition = course => !!course.position

  getIcon = () => {
    return {
      anchor: new window.google.maps.Point(15, 35),
      url: icon,
    }
  }
  
  getActiveIcon = () => {
    return {
      anchor: new window.google.maps.Point(15, 35),
      url: iconActive,
    }
  }

  drawCluster = (courses = [], activeCourses = [], finnishCourses = []) => {
    const {
      onMarkerClick,
      onMarkerMouseOver,
      onMarkerMouseOut,
      gridSize = 20, 
      minimumClusterSize = 3,
    } = this.props;

    const bounds = new window.google.maps.LatLngBounds();
    const markers = [...courses, ...finnishCourses]
      .map(course => ({...course, position: parseGeolocationForCourse(course)}))
      .filter(this.hasPosition)
      .map(course => {
        bounds.extend(new window.google.maps.LatLng(course.position.lat, course.position.lng));
        return course
      })
      .map(({position, name, id, redirect_fi}) => new window.google.maps.Marker({
        active: activeCourses.includes(parseInt(id,10)),
        position,
        name,
        id,
        redirect_fi,
        icon: this.getIcon(),
      }))
      .map(marker => {
        marker
          .addListener('click', function() {
            onMarkerClick(this.map, this, marker.name, marker.id, marker.redirect_fi)
          })
        marker
          .addListener('mouseover', function() {
            onMarkerMouseOver(this)
          });
        marker
          .addListener('mouseout', function() {
            onMarkerMouseOut(this)
          });
        return marker
      }
    )
    
    if(this.cluster && this.cluster.getMarkers().length === markers.length){
      if(activeCourses.length){
        const newActiveIds = markers.filter(({active}) => active).map(({id}) => parseInt(id, 10))
        const oldMarkers = this.cluster.getMarkers().filter(({id}) => newActiveIds.includes(parseInt(id,10)))
        oldMarkers.map((marker) => {
          const {
            url,
            anchor
          } = this.getActiveIcon();
          marker.setIcon({
            url,
            anchor
          })
         marker.active = true
         marker.setZIndex(window.google.maps.Marker.MAX_ZINDEX + 1)
         return marker
      })
      }
      else{
        const oldActiveMarkers = this.cluster.getMarkers().filter(({active}) => active)
        oldActiveMarkers.map((marker) => {
          const {
            anchor,
            url
          } = this.getIcon()
          marker.setIcon({
            anchor,
            url,
          })
          marker.active = false
          marker.setZIndex(undefined)
          return marker
        })
      }
      this.cluster.fitMapToMarkers()
    }
    else{
      this.cluster = new MarkerClusterer(this.map, markers, {
        gridSize,
        minimumClusterSize
      });
      this.map.fitBounds(bounds)
    }
  }

  loadMap = () => {
    if (window.google) {
      const { children, courses, activeCourses, ...rest } = this.props;
      const { finnishCourses } = this.state;
      const maps = window.google.maps;
      this.map = new maps.Map(this.mapNode, {
        ...rest,
        zoomControlOptions: {position: maps.ControlPosition.LEFT_BOTTOM},
        streetViewControlOptions: {position: maps.ControlPosition.LEFT_BOTTOM},
      });
      maps.event.addListenerOnce(this.map, 'bounds_changed', function() { this.setZoom(Math.min(14, this.getZoom())); });
      if(courses.length){
        this.drawCluster(courses, activeCourses, finnishCourses)
      }
    }
  }

  loadFinnishCourses = () => {
    if(process.env.REACT_APP_LOCALE === 'en'){
      fetch('https://app.birdietime.com/api-public/v2/courses')
        .then(
          (response) => {
            if(response.status === 200) {
              return response;
            }
            throw new Error(`${response.status} ${response.statusText}`);
          }
        )
        .then(response => response.json())
        .then(response => response.map(course => ({...course, 'redirect_fi': true})))
        .then(response => response.filter(course => course.organizers.length > 0))
        .then(response => {
          this.setState({
            finnishCourses: response
          })
        })
    }
  }

  renderChildren = () => {
    const { children = [] } = this.props;
    if(!children){
      return
    }
    return React.Children.map(children, c => {
      if (!c) return;
      return React.cloneElement(c, {
        map: this.map,
        google: window.google,
        mapCenter: this.props.center
      });
    })
  }

  render() {
    const {
      searchMap
    } = this.props
    return (
      <MapWrapper className="map--wrap" searchMap={searchMap} innerRef={(node) => this.mapNode = node}>
        {this.renderChildren()}
      </MapWrapper>
    )
  }
}

const MapWrapper = styled.div`
  height: calc(100vh - 60px);
  ${({searchMap}) => searchMap && `
    height: 100%;
  `}
`;


export default Map



// WEBPACK FOOTER //
// ./src/modules/common/components/map/Map.js